import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import RichMediaAnnouncementModal from './RichMediaAnnouncementModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnnouncementBanner = () => {
  const [banners, setBanners] = useState([]);
  const [modals, setModals] = useState([]);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    fetchActiveBanners();
  }, []);

  useEffect(() => {
    // Track impression when banner is shown
    if (banners.length > 0 && banners[currentIndex]) {
      trackImpression(banners[currentIndex].id);
    }
  }, [currentIndex, banners]);

  useEffect(() => {
    // Auto-rotate announcements
    if (banners.length > 1 && banners[currentIndex]?.auto_rotate) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, (banners[currentIndex]?.rotation_interval || 5) * 1000);

      return () => clearInterval(interval);
    }
  }, [banners, currentIndex]);

  const trackImpression = async (announcementId) => {
    try {
      await axios.post(`${API}/announcements/${announcementId}/track/impression`);
    } catch (error) {
      console.error('Failed to track impression:', error);
    }
  };

  const trackClick = async (announcementId) => {
    try {
      await axios.post(`${API}/announcements/${announcementId}/track/click`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  const trackDismissal = async (announcementId) => {
    try {
      await axios.post(`${API}/announcements/${announcementId}/track/dismissal`);
    } catch (error) {
      console.error('Failed to track dismissal:', error);
    }
  };

  const fetchActiveBanners = async () => {
    try {
      const response = await axios.get(`${API}/announcements/active`);
      const bannerAnnouncements = response.data.filter(a => a.type === 'banner');
      const modalAnnouncements = response.data.filter(a => a.type === 'modal');

      // Check if any banner was dismissed
      const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '{}');
      const dismissedModals = JSON.parse(localStorage.getItem('dismissedModals') || '{}');
      const now = Date.now();

      const visibleBanners = bannerAnnouncements.filter(banner => {
        const dismissInfo = dismissedBanners[banner.id];
        if (!dismissInfo) return true;

        // Check dismiss behavior
        if (banner.dismiss_behavior === 'never') return true;
        if (banner.dismiss_behavior === 'session') {
          // Session-based: check if same session
          const sessionId = sessionStorage.getItem('sessionId');
          return dismissInfo.sessionId !== sessionId;
        }
        if (banner.dismiss_behavior === '24hrs') {
          return now - dismissInfo.timestamp > 24 * 60 * 60 * 1000;
        }
        if (banner.dismiss_behavior === '7days') {
          return now - dismissInfo.timestamp > 7 * 24 * 60 * 60 * 1000;
        }
        return true;
      });

      const visibleModals = modalAnnouncements.filter(modal => {
        const dismissInfo = dismissedModals[modal.id];
        if (!dismissInfo) return true;

        // Check dismiss behavior
        if (modal.dismiss_behavior === 'never') return true;
        if (modal.dismiss_behavior === 'session') {
          const sessionId = sessionStorage.getItem('sessionId');
          return dismissInfo.sessionId !== sessionId;
        }
        if (modal.dismiss_behavior === '24hrs') {
          return now - dismissInfo.timestamp > 24 * 60 * 60 * 1000;
        }
        if (modal.dismiss_behavior === '7days') {
          return now - dismissInfo.timestamp > 7 * 24 * 60 * 60 * 1000;
        }
        return true;
      });

      setBanners(visibleBanners);
      setModals(visibleModals);
      setIsVisible(visibleBanners.length > 0);
      
      // Show modal after a delay if there are any visible modals
      if (visibleModals.length > 0) {
        setTimeout(() => {
          setShowModal(true);
          trackImpression(visibleModals[0].id);
        }, 2000); // Show modal 2 seconds after page load
      }
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const handleCtaClick = (e, banner) => {
    // Track the click
    trackClick(banner.id);
    
    // If link is to contact section, focus the full name field
    const link = banner.cta_link || '/#contact';
    if (link.includes('#contact')) {
      e.preventDefault();
      
      // Scroll to contact section with better positioning
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        // Scroll with offset to account for header
        const headerOffset = 80; // Height of fixed header
        const elementPosition = contactSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Focus on full name field after scroll
        setTimeout(() => {
          const nameInput = document.getElementById('name');
          if (nameInput) {
            nameInput.focus();
          }
        }, 800);
      }
    }
  };

  const handleDismiss = () => {
    if (banners.length === 0) return;

    const currentBanner = banners[currentIndex];
    
    // Track dismissal
    trackDismissal(currentBanner.id);
    
    const dismissedBanners = JSON.parse(localStorage.getItem('dismissedBanners') || '{}');
    
    // Store session ID for session-based dismissal
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}`;
      sessionStorage.setItem('sessionId', sessionId);
    }

    dismissedBanners[currentBanner.id] = {
      timestamp: Date.now(),
      sessionId: sessionId
    };
    
    localStorage.setItem('dismissedBanners', JSON.stringify(dismissedBanners));
    setIsVisible(false);
  };

  const handleModalClose = () => {
    if (modals.length === 0) return;

    const currentModal = modals[currentModalIndex];
    
    // Track dismissal
    trackDismissal(currentModal.id);
    
    const dismissedModals = JSON.parse(localStorage.getItem('dismissedModals') || '{}');
    
    // Store session ID for session-based dismissal
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}`;
      sessionStorage.setItem('sessionId', sessionId);
    }

    dismissedModals[currentModal.id] = {
      timestamp: Date.now(),
      sessionId: sessionId
    };
    
    localStorage.setItem('dismissedModals', JSON.stringify(dismissedModals));
    setShowModal(false);
    
    // Show next modal if available
    if (currentModalIndex + 1 < modals.length) {
      setTimeout(() => {
        setCurrentModalIndex(currentModalIndex + 1);
        setShowModal(true);
        trackImpression(modals[currentModalIndex + 1].id);
      }, 5000); // Show next modal after 5 seconds
    }
  };

  const handleModalCtaClick = (announcementId, isPrimary) => {
    trackClick(announcementId);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const getThemeStyles = (banner) => {
    const themes = {
      colorful: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#ffffff'
      },
      festive: {
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: '#ffffff'
      },
      professional: {
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        color: '#ffffff'
      },
      custom: {
        background: banner.background_color,
        color: banner.text_color
      }
    };

    return themes[banner.theme] || themes.professional;
  };

  // Only return null if there are no banners AND no modals
  if (!isVisible && banners.length === 0 && modals.length === 0) return null;

  const currentBanner = banners.length > 0 ? banners[currentIndex] : null;
  const themeStyles = currentBanner ? getThemeStyles(currentBanner) : null;

  return (
    <>
      {/* Banner Announcements */}
      {isVisible && banners.length > 0 && currentBanner && (
        <div 
          className="announcement-banner"
          style={{ 
            background: themeStyles.background,
            color: themeStyles.color 
          }}
        >
          <div className="announcement-banner-content">
            {banners.length > 1 && (
              <button 
                onClick={goToPrevious} 
                className="announcement-nav-btn"
                aria-label="Previous announcement"
              >
                <ChevronLeft size={20} />
              </button>
            )}

            <div className="announcement-message">
              {currentBanner.icon && <span className="announcement-icon">{currentBanner.icon}</span>}
              <span className="announcement-title">{currentBanner.title}</span>
              <span className="announcement-text">{currentBanner.message}</span>
              
              {currentBanner.cta_text && (
                <a 
                  href={currentBanner.cta_link || '/#contact'} 
                  className="announcement-cta"
                  style={{ color: themeStyles.color }}
                  onClick={(e) => handleCtaClick(e, currentBanner)}
                >
                  {currentBanner.cta_text}
                </a>
              )}
            </div>

            {banners.length > 1 && (
              <button 
                onClick={goToNext} 
                className="announcement-nav-btn"
                aria-label="Next announcement"
              >
                <ChevronRight size={20} />
              </button>
            )}

            <button 
              onClick={handleDismiss} 
              className="announcement-close"
              aria-label="Dismiss announcement"
            >
              <X size={18} />
            </button>
          </div>

          {banners.length > 1 && (
            <div className="announcement-dots">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`announcement-dot ${index === currentIndex ? 'active' : ''}`}
                  aria-label={`Go to announcement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Announcements */}
      {showModal && modals.length > 0 && modals[currentModalIndex] && (
        <RichMediaAnnouncementModal
          announcement={modals[currentModalIndex]}
          onClose={handleModalClose}
          onCtaClick={handleModalCtaClick}
        />
      )}
    </>
  );
};

export default AnnouncementBanner;
